import { isNumeric } from "metabase-lib/v1/types/utils/isa";
import type { DatasetData, RowValue, RowValues } from "metabase-types/api";

export function dimensionIsNumeric(
  { cols, rows }: DatasetData,
  i = 0,
): boolean {
  if (isNumeric(cols[i])) {
    return true;
  }

  const hasAtLeastOneNumber = rows.some((row) => typeof row[i] === "number");
  const hasNumbersOrNullsOnly = rows.every(
    (row) => typeof row[i] === "number" || row[i] === null,
  );

  return hasNumbersOrNullsOnly && hasAtLeastOneNumber;
}

export const isMultipleOf = (value: number, base: number): boolean => {
  // Ideally we could use Number.EPSILON as constant diffThreshold here.
  // However, we sometimes see very small errors that are bigger than EPSILON.
  // For example, when called 1.23456789 and 1e-8 we see a diff of ~1e-16.
  const diffThreshold = Math.pow(10, SMALLEST_PRECISION_EXP);
  return Math.abs(value - Math.round(value / base) * base) < diffThreshold;
};

// We seem to run into float bugs if we get any more precise than this.
const SMALLEST_PRECISION_EXP = -13;

export function precision(a: RowValue): number {
  if (typeof a === "string") {
    return precision(Number(a));
  }

  if (
    a == null ||
    typeof a === "boolean" ||
    typeof a === "object" ||
    !isFinite(a)
  ) {
    return 0;
  }

  if (!a) {
    return 0;
  }

  // Find the largest power of ten needed to evenly divide the value. We start
  // with the power of ten greater than the value and walk backwards until we
  // hit our limit of SMALLEST_PRECISION_EXP or isMultipleOf returns true.
  let e = Math.ceil(Math.log10(Math.abs(a)));
  while (e > SMALLEST_PRECISION_EXP && !isMultipleOf(a, Math.pow(10, e))) {
    e--;
  }
  return Math.pow(10, e);
}

export function computeNumericDataInterval(xValues: RowValues): number {
  let bestPrecision = Infinity;
  for (const value of xValues) {
    const p = precision(value) || 1;
    if (p < bestPrecision) {
      bestPrecision = p;
    }
  }
  return bestPrecision;
}

export function computeChange(comparisonVal: number, currVal: number): number {
  if (comparisonVal === 0) {
    return currVal === 0 ? 0 : currVal > 0 ? Infinity : -Infinity;
  }

  return (currVal - comparisonVal) / Math.abs(comparisonVal);
}
